import { RefObject, useMemo, useRef } from 'react';
import theme from '@wavetrak/theme';
import useFullscreen from 'hooks/useFullscreen';
import { RewindCamPlayerHandle } from 'components/RewindCamPlayer/RewindCamPlayer';
import { VideoPlayerHandle } from 'components/VideoPlaylistPlayer/VideoPlaylistPlayer';
import { LiveCamPlayerHandle } from '../CamPlayerKbygRewindsTypes';
import { Player } from '../usePlayerState/usePlayerState';

interface UseKbygRewindsFullscreenProps {
  width: number;
  height: number;
  multiCamViewActive: boolean;
  currentPreviewPlayer: Player;
  liveCamPlayerHandle: RefObject<LiveCamPlayerHandle | VideoPlayerHandle>;
  rewindCamPlayerHandle: RefObject<RewindCamPlayerHandle | VideoPlayerHandle>;
}

const useKbygRewindsFullscreen = ({
  width,
  height,
  multiCamViewActive,
  currentPreviewPlayer,
  liveCamPlayerHandle,
  rewindCamPlayerHandle,
}: UseKbygRewindsFullscreenProps) => {
  const fullscreenElementRef = useRef(null);

  const [isFullscreen, toggleFullscreenApi, isFullscreenApiAvailable] = useFullscreen(
    !multiCamViewActive && fullscreenElementRef,
  );

  // player is full width below tabletLg and has a 100:72 aspect ratio so accounting for the possible gain in window height
  // when going fullscreen, it ends up being a worse experience when the height is roughly less than 65% of the width
  const supportsFullscreen = useMemo(() => {
    if (isFullscreen) return true;
    if (width >= theme.breakpoints.values.tabletLg) return true;
    return height > 0.65 * width;
  }, [height, isFullscreen, width]);

  // Note: this has to be wrapped in useMemo instead of useCallback otherwise this error appears:
  // "Failed to execute 'requestFullscreen' on 'Element': API can only be initiated by a user gesture."
  const handleRequestFullscreen = useMemo(() => {
    if (!supportsFullscreen) return () => {};

    // Important: fscreen doesn't work on iOS due to the fullscreen API not being fully supported. To workaround
    // this we can use the videojs full screen methods directly which we expose through `toggleNativeFullscreen`.
    return () => {
      if (!supportsFullscreen) return;

      if (isFullscreenApiAvailable) {
        toggleFullscreenApi();
        return;
      }

      if (currentPreviewPlayer === Player.LIVE) {
        liveCamPlayerHandle?.current?.toggleNativeFullscreen?.();
        return;
      }

      rewindCamPlayerHandle?.current?.toggleNativeFullscreen?.();
    };
  }, [
    currentPreviewPlayer,
    isFullscreenApiAvailable,
    liveCamPlayerHandle,
    supportsFullscreen,
    toggleFullscreenApi,
    rewindCamPlayerHandle,
  ]);

  return {
    fullscreenElementRef,
    isFullscreen,
    supportsFullscreen,
    handleRequestFullscreen,
  };
};

export default useKbygRewindsFullscreen;
